/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { setKey } from 'react-geocode';
import { AlertColor } from '@mui/material';

import { mileInMeters, Paths, SnackbarSeverity } from '../../../../../Shared/Constants/App';

import { CurrentAddress } from '../../../../../Shared/Components/Common/GoogleMapLocationSelector/GoogleMapLocationSelector.Model';

import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import { MapLoader } from '../../../../../Shared/Components/Common/MapLoader/MapLoader';
import SelectedLocationCard from './SelectedLocationCard';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import GoogleMapLocationSelector from '../../../../../Shared/Components/Common/GoogleMapLocationSelector/GoogleMapLocationSelector';

import { LocationCardContainer } from './ModifyGeoFence.Style';

const LatLngSelector = (): JSX.Element => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const lat = parseFloat(searchParams.get('lat') || '0') || 0;
  const lng = parseFloat(searchParams.get('lng') || '0') || 0;
  const addressExist = searchParams.get('addressExists') === 'true';

  const [currentLocation, setCurrentLocation] = useState({ lat: 0, lng: 0 });
  const [loading, setLoading] = useState<boolean>(false);
  const [addressExists, setAddressExists] = useState<boolean>(addressExist || false);
  const [currentAddress, setCurrentAddress] = useState<CurrentAddress>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>(SnackbarSeverity.success);
  const [isError, setIsError] = useState(false);
  const { t } = useTranslation(['jobManagement']);

  const navigate = useNavigate();

  const selectedRows = location.state?.selectedRows || [];

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  const handleSaveLatLng = (lat?: number, lng?: number) => {
    const updatedRows = [...selectedRows];
    const geoFenceRadius = location?.state?.modifiedRadius * mileInMeters;

    if (updatedRows[0]) {
      updatedRows[0].latitude = lat;
      updatedRows[0].longitude = lng;
    }

    navigate(Paths.ModifyGeoFence, {
      state: {
        latitude: lat,
        longitude: lng,
        geoFenceRadius: geoFenceRadius,
        formattedAddress: currentAddress,
        editLatLng: true,
        selectedRows: updatedRows,
      },
    });
  };

  useEffect(() => {
    setLoading?.(true);
    setKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: lat || position.coords.latitude,
            lng: lng || position.coords.longitude,
          });
          setLoading?.(false);
        },
        (error) => {
          console.error(t('googleMap:errorWhileFetchingCurrentLocation'), error);
          setLoading?.(false);
        }
      );
    } else {
      console.error(t('googleMap:geolocationNotSupportedByBrowser'));
      setLoading?.(false);
    }
  }, []);

  return (
    <>
      <PageTitle title={t('jobManagement:selectLocation')} />

      {loading || !currentLocation?.lat || !currentLocation?.lng ? (
        <MapLoader />
      ) : (
        <GoogleMapLocationSelector
          currentLocation={currentLocation}
          setCurrentLocation={setCurrentLocation}
          setCurrentAddress={setCurrentAddress}
          zoomValue={10}
          setIsError={setIsError}
          showSnackbar={showSnackbar}
          addressExists={addressExists}
          setAddressExists={setAddressExists}
          showSearchInput={true}
        />
      )}
      <LocationCardContainer>
        <SelectedLocationCard
          currentAddress={(currentAddress && currentAddress?.formattedAddress) || ''}
          loading={loading}
          latitude={currentAddress ? currentAddress?.latitude : null}
          longitude={currentAddress ? currentAddress?.longitude : null}
          isError={isError}
          handleSubmit={handleSaveLatLng}
        />
      </LocationCardContainer>
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default LatLngSelector;
